import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import screensize from '../hooks/resize';
import Header from "./header";
import SideNav from "./side-nav";
import BottomNav from "./BottomNav";
import OfflineBanner from "../components/common/OfflineBanner";
import { getUserRole, getPostLwdNotice } from '../helpers/tokenService';

const PostLwdNoticeBanner = () => {
  const notice = getPostLwdNotice();
  if (!notice || !notice.isGracePeriod) return null;

  const remaining = notice.remainingDays;
  const isLastDay = remaining <= 1;

  return (
    <div 
      style={{
        backgroundColor: isLastDay ? '#FFF2F2' : '#EFF6FF',
        border: `1px solid ${isLastDay ? '#FCA5A5' : '#BFDBFE'}`,
        color: isLastDay ? '#991B1B' : '#1E40AF',
        padding: '12px 20px',
        margin: '0 0 16px 0',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 5px rgba(0,0,0,0.04)',
        fontSize: '0.95rem',
        zIndex: 90
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '1.25rem' }}>{isLastDay ? '💖' : '📢'}</span>
        <span>
          {isLastDay ? (
            <strong>Today is your last day to access the portal. We wish you great success, happiness, and blessings for your future journey!</strong>
          ) : (
            remaining === 15 ? (
              <strong>You have 15 days only for access the portal.</strong>
            ) : (
              <strong>You have {remaining} days left to access the portal.</strong>
            )
          )}
        </span>
      </div>
      <span style={{ fontSize: '0.85rem', opacity: 0.9, fontStyle: 'italic' }}>
        (Access available for My Exit Documents, Salary Slips, & Form 16)
      </span>
    </div>
  );
};

const tabToRouteMap = {
  // Admin tabs
  "dashboard": "/dashboard",
  "On board": "/onboard",
  "Create letter": "/onboard/create-letter",
  "Document entry": "/onboard/document-entry",
  "Assets details": "/onboard/assets-details",
  "Assets assignment": "/onboard/assets-assignment",
  "All On board ": "/onboard/all",
  "Offer letter": "/onboard/offer-letter",
  "Offer letter log": "/onboard/offer-letter-log",
  "Referrals": "/onboard/referral-requests",
  "ActiveJobs": "/active-jobs",
  "Employees": "/employees",
  "Employee master": "/employees/employee-master",
  "Salary master": "/employees/salary-master",
  "Employee details": "/employees/employee-details",
  "CTC / earning": "/employees/ctc-earning",
  "Category": "/employees/category",
  "Calendar": "/employees/calendar",
  "All employees": "/employees/all",
  "Attendence": "/attendance",
  "Attendence regularization": "/attendance/regularization",
  "Submit attendance": "/attendance/submit",
  "In out activities": "/attendance/in-out-activities",
  "Daily attendence": "/attendance/daily-attendance",
  "Monthly attendence": "/attendance/monthly-attendance",
  "Manage shift": "/attendance/manage-shift",
  "All Attendence": "/attendance/all",
  "View Uploaded Attendance": "/attendance/view-uploaded",
  "Leave": "/leave",
  "Apply leave": "/leave/apply",
  "Leave balance": "/leave/balance",
  "All Leaves": "/leave/all",
  "Payroll": "/payroll",
  "Salary process": "/payroll/salary-process",
  "Final settlement": "/payroll/final-settlement",
  "Bank transfer": "/payroll/bank-transfer",
  "Salary paySilp": "/payroll/salary-payslip",
  "Form 16": "/payroll/form16",
  "Salary register": "/payroll/salary-register",
  "Reimbursement": "/payroll/reimbursement",
  "All payroll": "/payroll/all",
  "Loans": "/loans",
  "Survey": "/survey",
  "Settings": "/settings",
  "Settings-Organization": "/settings?menu=Organization",
  "Settings-Attendance": "/settings?menu=Attendance",
  "Settings-Leave": "/settings?menu=Leave",
  "Settings-Payroll": "/settings?menu=Payroll",
  "Settings-Notifications": "/settings?menu=Notifications",
  "Settings-Password": "/settings?menu=Password",
  "EditEmployee": "/employees/edit-employee",
  "BankDetail": "/payroll/add-bank-details",
  "LeaveCalendar": "/leave/calendar",
  "EPF": "/complains/epf",
  "ESIC": "/complains/esic",
  "Holidays": "/holidays",
  "Leave Policy": "/leave/policy",
  "Approvals": "/approvals",
  "Approved Leaves": "/approved-leaves",
  "Announcements": "/employees/announcements",
  "Profile": "/profile",
  "ProfileRequests": "/employees/profile-requests",

  // Employee tabs
  "PunchTime": "/punch",
  "Attendance Regularization": "/attendance/regularization",
  "Attendence Regularization": "/attendance/regularization",
  "AttendenceRegularization": "/attendance/regularization",
  "salarygenerate": "/payroll/salary-payslip",
  "EmpSalarySlip": "/payroll/salary-slip",
  "EmpSalaryStructure": "/payroll/salary-structure",
  "EmpOfferLetter": "/payroll/offer-letter",
  "EmpConfirmationLetter": "/payroll/confirmation-letter",
  "EmpExitDocuments": "/payroll/exit-documents",
  "Change Password": "/change-password",
  "Referral Request": "/referral-request",
  "companyinfo": "/company/info",
  "companycategory": "/company/category",
  "companyuploademp": "/company/upload-emp",
  "categorybranch": "/company/branch",
  "categorydepartment": "/company/department",
  "categorydesignation": "/company/designation",
};

const routeToTabMap = Object.entries(tabToRouteMap).reduce((acc, [tab, route]) => {
  acc[route] = tab;
  return acc;
}, {});

const Layout = () => {
  const userRole = getUserRole();
  const { screenWidth } = screensize();
  const [toggle, setToggle] = useState(screenWidth < 900);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setToggle(screenWidth < 900);
  }, [screenWidth]);

  useEffect(() => {
    if (toggle) {
      document.body.classList.add('inactive-nav');
    } else {
      document.body.classList.remove('inactive-nav');
    }
  }, [toggle]);

  // Derived current tab name to keep SideNav legacy active classes working if needed
  let currentTab = routeToTabMap[location.pathname] || "dashboard";
  if (location.pathname === "/settings") {
    const searchParams = new URLSearchParams(location.search);
    const menu = searchParams.get("menu");
    if (menu) {
      currentTab = `Settings-${menu}`;
    }
  }

  const handlePageNavigation = (tabName) => {
    const route = tabToRouteMap[tabName];
    if (route) {
      navigate(route);
    } else {
      console.warn(`No route configured for tab: ${tabName}`);
    }
  };

  return (
    <div className="container-fluid ot-div">
      <OfflineBanner />
      <Header toggle={toggle} setToggle={setToggle} setCurrentTab={handlePageNavigation} />
      
      {/* Backdrop for mobile drawer */}
      {screenWidth < 900 && !toggle && (
        <div 
          className="sidebar-backdrop" 
          onClick={() => setToggle(true)} 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(2px)',
            zIndex: 97,
            transition: 'opacity 0.2s ease-in-out'
          }}
        />
      )}

      <SideNav onClick={handlePageNavigation} setToggle={setToggle} role={userRole} currentTab={currentTab} />
      
      <main className="mn-container userLoggedin">
        <PostLwdNoticeBanner />
        <div className="main-content">
          <Outlet context={{ setCurrentTab: handlePageNavigation }} />
        </div>
      </main>

      {/* Render Bottom Navigation on Mobile Devices */}
      {screenWidth < 900 && (
        <BottomNav 
          role={userRole} 
          currentTab={currentTab} 
          onClick={handlePageNavigation} 
          onMenuToggle={() => setToggle(!toggle)}
          sidebarOpen={!toggle}
        />
      )}
    </div>
  );
};

export default Layout;
